import i18n from '@/i18n'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'

class ApiClient {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    }

    try {
      const response = await fetch(url, config)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error)
      throw error
    }
  }

  /**
   * Get current language
   */
  private getLanguage(): string {
    return i18n.language || 'en'
  }

  /**
   * Generate context questions for a given prompt
   */
  async generateContextQuestions(originalPrompt: string): Promise<{
    success: boolean
    questions: string[]
    error?: string
  }> {
    return this.request('/context/questions', {
      method: 'POST',
      body: JSON.stringify({
        originalPrompt,
        language: this.getLanguage()
      }),
    })
  }

  /**
   * Generate context output from user input and questions
   */
  async generateContextOutput(
    originalPrompt: string,
    questions: string[],
    userInput: string
  ): Promise<{
    success: boolean
    contextOutput: string
    error?: string
  }> {
    return this.request('/context/output', {
      method: 'POST',
      body: JSON.stringify({
        originalPrompt,
        questions,
        userInput,
        language: this.getLanguage()
      }),
    })
  }

  /**
   * Generate role suggestions for a given prompt
   */
  async generateRoleSuggestions(originalPrompt: string): Promise<{
    success: boolean
    roles: string[]
    error?: string
  }> {
    return this.request('/role/suggestions', {
      method: 'POST',
      body: JSON.stringify({
        originalPrompt,
        language: this.getLanguage()
      }),
    })
  }

  /**
   * Generate action instruction for a given prompt
   */
  async generateActionInstruction(originalPrompt: string): Promise<{
    success: boolean
    action: string
    error?: string
  }> {
    return this.request('/action/generate', {
      method: 'POST',
      body: JSON.stringify({
        originalPrompt,
        language: this.getLanguage()
      }),
    })
  }

  /**
   * Generate final comprehensive prompt from all CRAFT elements
   */
  async generateFinalPrompt(
    originalPrompt: string,
    contextOutput: string,
    selectedRole: string,
    action: string,
    format: string,
    tone: string,
    example: string
  ): Promise<{
    success: boolean
    finalPrompt: string
    error?: string
  }> {
    return this.request('/refine/generate', {
      method: 'POST',
      body: JSON.stringify({
        originalPrompt,
        contextOutput,
        selectedRole,
        action,
        format,
        tone,
        example,
        language: this.getLanguage()
      }),
    })
  }

  /**
   * Test OpenAI connection
   */
  async testOpenAIConnection(): Promise<{
    success: boolean
    connected: boolean
    error?: string
  }> {
    return this.request('/context/test')
  }
}

export const ApiService = new ApiClient()
